import { Grid, Typography } from '@mui/material';
import { AdditionalControlsProps } from 'components/Procedure/Infobar/GroupedLayersVisualControls';
import {
  getSettingsKeyFromLayerIdComponents,
  LayerIdComponentProps,
  LayerVisualizationSettings,
  slidesLayerVisualizationSettings,
  useApplyChangesToSlideLayerVisualizationSettings,
} from 'components/Procedure/Infobar/slidesVisualizationAndConfiguration';
import { FeatureMetadata } from 'components/Procedure/useSlideChannelsAndResults/featureMetadata';
import {
  compact,
  concat,
  filter,
  first,
  isEmpty,
  join,
  keys,
  map,
  size,
  some,
  startsWith,
  uniq,
} from 'es-toolkit/compat';
import { Job } from 'interfaces/job';
import React, { useCallback, useEffect, useMemo } from 'react';

export const getHeatmapUrlKey = ({
  parentKey,
  layerName,
  orchestrationId, // Required for internal heatmaps
}: {
  parentKey?: string;
  layerName?: string;
  orchestrationId?: string; // Required for internal heatmaps
}) => {
  if (!layerName && !parentKey) {
    return '';
  }
  const keyWithoutOrchId = getSettingsKeyFromLayerIdComponents({ parentKey, layerName });
  // Multiple internal heatmaps can have the same layer key, so we need to use the orchestration id to differentiate them
  return orchestrationId ? `${orchestrationId}-${keyWithoutOrchId}` : `${keyWithoutOrchId}`;
};

export const getHeatmapUrlKeyFromFeatureMetadata = ({
  heatmap,
  parentHeatmap,
  addOrchestrationId, // Required for internal heatmaps
}: {
  heatmap: FeatureMetadata;
  parentHeatmap?: Partial<Pick<FeatureMetadata, 'id' | 'key' | 'orchestrationId'>>;
  addOrchestrationId?: boolean;
}) => {
  if (!heatmap) {
    return '';
  }
  const parentKey = parentHeatmap?.key || parentHeatmap?.id;
  const layerName = heatmap.key || heatmap.id;
  const orchestrationId = addOrchestrationId ? heatmap.orchestrationId || parentHeatmap?.orchestrationId : undefined;
  return getHeatmapUrlKey({ parentKey, layerName, orchestrationId });
};

export const useInitialExpandIfHeatmapSelected = ({
  expandAccordion,
  setExpandAccordion,
  heatmaps,
  viewerIndex,
  slideId,
}: {
  expandAccordion: boolean;
  setExpandAccordion: (expand: boolean) => void;
  heatmaps: FeatureMetadata[];
  viewerIndex: number;
  slideId: string;
}) => {
  // On first render, set the default expanded state based on selected heatmaps
  useEffect(() => {
    if (expandAccordion) {
      return;
    }
    const viewerSlideLayerVisualizationSettings = slidesLayerVisualizationSettings[viewerIndex];
    const slideLayerVisualizationSettings = viewerSlideLayerVisualizationSettings?.value?.[slideId];

    const hasCurrentlySelectedHeatmaps = some(
      heatmaps,
      ({ id }) => slideLayerVisualizationSettings?.[id]?.value?.selected
    );
    if (hasCurrentlySelectedHeatmaps) {
      setExpandAccordion(hasCurrentlySelectedHeatmaps);
    }
  }, [heatmaps, viewerIndex, slideId]);
};

export const heatmapToSubtitle = ({
  heatmap,
  showFileTypes,
}: {
  heatmap: FeatureMetadata;
  showFileTypes?: boolean;
}) => {
  const fileType = heatmap.heatmapType;
  return (
    showFileTypes && (
      <Grid item container wrap="nowrap" overflow="hidden" maxWidth="100%" alignItems="center">
        {fileType && (
          <Grid item mr={1}>
            <Typography
              variant="caption"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              component="div"
              maxWidth="100%"
              color="text.secondary"
            >
              ({fileType})
            </Typography>
          </Grid>
        )}
      </Grid>
    )
  );
};

export const useHandleVisualSettingsUpdate = ({
  viewerIndex,
  slideId,
  parentKey,
  layerName,
  layerUrlKey,
  layerIdToProps,
  stainTypeId,
}: {
  viewerIndex: number;
  slideId: string;
  layerUrlKey?: string;
  layerIdToProps: AdditionalControlsProps['layerIdToProps'];
  stainTypeId: string;
} & LayerIdComponentProps) => {
  if (!layerName && !parentKey) {
    console.warn('Either layerName or parentKey must be provided for useHandleVisualSettingsUpdate');
  }

  const applyChangesToSlideLayerVisualizationSettings = useApplyChangesToSlideLayerVisualizationSettings();

  const handleVisualSettingsUpdate = useCallback(
    (getNewSettings: (oldSettings: LayerVisualizationSettings) => Partial<LayerVisualizationSettings>) => {
      const viewerSlideLayerVisualizationSettings = slidesLayerVisualizationSettings[viewerIndex];
      if (!viewerSlideLayerVisualizationSettings) {
        console.warn(`Invalid viewerIndex: ${viewerIndex}`);
        return;
      }
      const slideLayerVisualizationSettings = viewerSlideLayerVisualizationSettings.value?.[slideId];
      if (!slideLayerVisualizationSettings) {
        console.warn(`Invalid slideId: ${slideId}`);
        return;
      }

      const relevantLayers =
        parentKey && layerName
          ? [getSettingsKeyFromLayerIdComponents({ parentKey, layerName })]
          : parentKey
          ? filter(keys(slideLayerVisualizationSettings), (key) => startsWith(key, parentKey))
          : layerName
          ? [layerName]
          : [];

      const changes = map(relevantLayers, (layerId) => {
        const previousLayerSettings = slideLayerVisualizationSettings[layerId]?.value;
        const newSettings = getNewSettings(previousLayerSettings);
        return {
          layerId,
          newSettings,
          layerUrlKey: layerUrlKey ?? layerIdToProps?.[layerId]?.urlKey,
        };
      });
      if (!isEmpty(changes)) {
        return applyChangesToSlideLayerVisualizationSettings({
          slideId,
          viewerIndex,
          stainTypeId,
          changes,
          changeFlow: `handleChange-${join(relevantLayers, '+')}`,
        });
      }
    },
    [
      applyChangesToSlideLayerVisualizationSettings,
      viewerIndex,
      slideId,
      parentKey,
      layerName,
      layerIdToProps,
      stainTypeId,
    ]
  );

  return { handleVisualSettingsUpdate };
};

export const useJobForOrchestrationFeatureMetadata = (
  heatmaps: FeatureMetadata[],
  nonHeatmapFeatures?: FeatureMetadata
): Partial<Job> =>
  useMemo(() => {
    const allJobs = compact(map(concat(heatmaps, nonHeatmapFeatures), 'job'));
    const uniqueJobIds = uniq(map(allJobs, 'id'));
    if (size(uniqueJobIds) > 1) {
      console.warn(
        'Multiple jobs found for features/heatmaps, using the first one. This may lead to unexpected behavior.',
        uniqueJobIds,
        { nonHeatmapFeatures, heatmaps }
      );
    }
    return first(allJobs);
  }, [heatmaps, nonHeatmapFeatures]);
